<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    
    <div class="row">
      <!-- Configurações de Unidade -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Configurações de Unidade FHIR</h4>
                <p class="card-title-desc">
                  Configure a integração FHIR com e-SUS e RNDS para a unidade de saúde
                </p>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    @click="showCreateUnitModal = true" 
                    class="btn btn-success float-right"
                    :disabled="loading"
                  >
                    <i class="ri-add-line me-1"></i>
                    Nova Configuração
                  </button>
                </div>
              </div>
            </div>

            <div v-if="loadingUnits" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Carregando configurações...</p>
            </div>

            <div v-else-if="unitConfigs.length === 0" class="text-center py-5">
              <div class="mb-3">
                <i class="ri-settings-3-line font-size-48 text-muted"></i>
              </div>
              <h5 class="text-muted">Nenhuma configuração encontrada</h5>
              <p class="text-muted">Clique em "Nova Configuração" para adicionar a primeira configuração FHIR</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-centered table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Unidade</th>
                    <th>CNES</th>
                    <th>RNDS</th>
                    <th>e-SUS</th>
                    <th>Status</th>
                    <th>Padrão</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="config in unitConfigs" :key="config.id">
                    <td>
                      <h6 class="mb-0">{{ config.nome_unidade }}</h6>
                      <small class="text-muted">{{ config.codigo_ibge }}</small>
                    </td>
                    <td>
                      <span class="badge bg-soft-info text-info">{{ config.cnes }}</span>
                    </td>
                    <td>
                      <span 
                        :class="config.rnds_endpoint ? 'badge bg-soft-success text-success' : 'badge bg-soft-secondary text-secondary'"
                      >
                        {{ config.rnds_endpoint ? 'Configurado' : 'Não configurado' }}
                      </span>
                    </td>
                    <td>
                      <span 
                        :class="config.esus_endpoint ? 'badge bg-soft-success text-success' : 'badge bg-soft-secondary text-secondary'"
                      >
                        {{ config.esus_endpoint ? 'Configurado' : 'Não configurado' }}
                      </span>
                    </td>
                    <td>
                      <span 
                        :class="config.is_active ? 'badge bg-soft-success text-success' : 'badge bg-soft-danger text-danger'"
                      >
                        {{ config.is_active ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td>
                      <span v-if="config.is_default" class="badge bg-soft-primary text-primary">
                        <i class="ri-star-fill me-1"></i>Padrão
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" 
                                data-bs-toggle="dropdown" aria-expanded="false">
                          Ações
                        </button>
                        <ul class="dropdown-menu">
                          <li>
                            <a class="dropdown-item" href="#" @click="editUnitConfig(config)">
                              <i class="ri-edit-line me-2"></i>Editar
                            </a>
                          </li>
                          <li>
                            <a class="dropdown-item" href="#" @click="toggleUnitDefault(config)">
                              <i class="ri-star-line me-2"></i>{{ config.is_default ? 'Remover padrão' : 'Definir como padrão' }}
                            </a>
                          </li>
                          <li><hr class="dropdown-divider"></li>
                          <li>
                            <a class="dropdown-item text-danger" href="#" @click="deleteUnitConfig(config)">
                              <i class="ri-delete-bin-line me-2"></i>Excluir
                            </a>
                          </li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- Configurações de Profissionais -->
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="row mb-3">
              <div class="col-sm-6">
                <h4 class="card-title">Configurações de Profissionais</h4>
                <p class="card-title-desc">
                  Configure dados específicos de profissionais para integração FHIR
                </p>
              </div>
              <div class="col-sm-6">
                <div class="text-sm-end">
                  <button 
                    @click="showCreateProfessionalModal = true" 
                    class="btn btn-primary float-right"
                    :disabled="loading"
                  >
                    <i class="ri-user-add-line me-1"></i>
                    Configurar Profissional
                  </button>
                </div>
              </div>
            </div>

            <div v-if="loadingProfessionals" class="text-center py-4">
              <div class="spinner-border text-primary" role="status"></div>
              <p class="mt-2">Carregando profissionais...</p>
            </div>

            <div v-else-if="professionalConfigs.length === 0" class="text-center py-5">
              <div class="mb-3">
                <i class="ri-user-settings-line font-size-48 text-muted"></i>
              </div>
              <h5 class="text-muted">Nenhum profissional configurado</h5>
              <p class="text-muted">Configure profissionais para habilitá-los na integração FHIR</p>
            </div>

            <div v-else class="table-responsive">
              <table class="table table-centered table-nowrap mb-0">
                <thead class="table-light">
                  <tr>
                    <th>Profissional</th>
                    <th>CPF</th>
                    <th>CNS</th>
                    <th>CRM</th>
                    <th>Especialidade</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="config in professionalConfigs" :key="config.id">
                    <td>
                      <h6 class="mb-0">{{ config.nome_profissional }}</h6>
                      <small class="text-muted">ID: {{ config.user_id }}</small>
                    </td>
                    <td>{{ formatCPF(config.cpf_profissional) }}</td>
                    <td>{{ config.cns_profissional }}</td>
                    <td>{{ config.crm_profissional || '-' }}</td>
                    <td>{{ config.especialidade_nome || '-' }}</td>
                    <td>
                      <span 
                        :class="config.is_active ? 'badge bg-soft-success text-success' : 'badge bg-soft-danger text-danger'"
                      >
                        {{ config.is_active ? 'Ativo' : 'Inativo' }}
                      </span>
                    </td>
                    <td>
                      <button class="btn btn-sm btn-outline-primary me-1" @click="editProfessionalConfig(config)">
                        <i class="ri-edit-line"></i>
                      </button>
                      <button class="btn btn-sm btn-outline-danger" @click="deleteProfessionalConfig(config)">
                        <i class="ri-delete-bin-line"></i>
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal para Criar/Editar Configuração de Unidade -->
    <b-modal
      v-model="showCreateUnitModal"
      :title="editingUnit ? 'Editar Configuração de Unidade' : 'Nova Configuração de Unidade'"
      size="lg"
      @hidden="resetUnitForm"
    >
      <form @submit.prevent="saveUnitConfig">
        <!-- Dados da Unidade -->
        <div class="mb-4">
          <h6 class="mb-3">Dados da Unidade</h6>
          
          <div class="row">
            <div class="col-md-8">
              <b-form-group label="Nome da Unidade" label-for="nome_unidade">
                <b-form-input
                  id="nome_unidade"
                  v-model="unitForm.nome_unidade"
                  placeholder="Nome da unidade de saúde"
                  required
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="CNES" label-for="cnes">
                <b-form-input
                  id="cnes"
                  v-model="unitForm.cnes"
                  placeholder="0000000"
                  maxlength="7"
                  required
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="INE" label-for="ine">
                <b-form-input
                  id="ine"
                  v-model="unitForm.ine"
                  placeholder="0000000000"
                  maxlength="10"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Código IBGE" label-for="codigo_ibge">
                <b-form-input
                  id="codigo_ibge"
                  v-model="unitForm.codigo_ibge"
                  placeholder="000000"
                  maxlength="7"
                  required
                />
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Configurações RNDS -->
        <div class="mb-4">
          <h6 class="mb-3">Configurações RNDS</h6>
          
          <div class="row">
            <div class="col-12">
              <b-form-group label="Endpoint RNDS" label-for="rnds_endpoint">
                <b-form-input
                  id="rnds_endpoint"
                  v-model="unitForm.rnds_endpoint"
                  placeholder="https://rnds.saude.gov.br"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Client ID" label-for="rnds_client_id">
                <b-form-input
                  id="rnds_client_id"
                  v-model="unitForm.rnds_client_id"
                  placeholder="Client ID para RNDS"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Client Secret" label-for="rnds_client_secret">
                <b-form-input
                  id="rnds_client_secret"
                  v-model="unitForm.rnds_client_secret"
                  type="password"
                  placeholder="Client Secret para RNDS"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Ambiente" label-for="rnds_environment">
                <b-form-select
                  id="rnds_environment"
                  v-model="unitForm.rnds_environment"
                  :options="[
                    { value: 'homolog', text: 'Homologação' },
                    { value: 'production', text: 'Produção' }
                  ]"
                />
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Configurações e-SUS -->
        <div class="mb-4">
          <h6 class="mb-3">Configurações e-SUS</h6>
          
          <div class="row">
            <div class="col-12">
              <b-form-group label="Endpoint e-SUS" label-for="esus_endpoint">
                <b-form-input
                  id="esus_endpoint"
                  v-model="unitForm.esus_endpoint"
                  placeholder="https://esus.saude.gov.br"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-4">
              <b-form-group label="Usuário" label-for="esus_usuario">
                <b-form-input
                  id="esus_usuario"
                  v-model="unitForm.esus_usuario"
                  placeholder="Usuário e-SUS"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Senha" label-for="esus_senha">
                <b-form-input
                  id="esus_senha"
                  v-model="unitForm.esus_senha"
                  type="password"
                  placeholder="Senha e-SUS"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Counter ID" label-for="esus_instalacao_counter_id">
                <b-form-input
                  id="esus_instalacao_counter_id"
                  v-model="unitForm.esus_instalacao_counter_id"
                  placeholder="ID do contador"
                />
              </b-form-group>
            </div>
          </div>
        </div>

        <!-- Configurações Gerais -->
        <div class="mb-4">
          <h6 class="mb-3">Configurações Gerais</h6>
          
          <div class="row">
            <div class="col-md-8">
              <b-form-group label="URL Base FHIR" label-for="fhir_base_url">
                <b-form-input
                  id="fhir_base_url"
                  v-model="unitForm.fhir_base_url"
                  placeholder="https://fhir.example.com"
                />
              </b-form-group>
            </div>
            <div class="col-md-4">
              <b-form-group label="Timezone" label-for="timezone">
                <b-form-select
                  id="timezone"
                  v-model="unitForm.timezone"
                  :options="[
                    { value: 'America/Sao_Paulo', text: 'America/São Paulo' },
                    { value: 'America/Manaus', text: 'America/Manaus' },
                    { value: 'America/Fortaleza', text: 'America/Fortaleza' }
                  ]"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-checkbox v-model="unitForm.is_active">
                Configuração ativa
              </b-form-checkbox>
            </div>
            <div class="col-md-6">
              <b-form-checkbox v-model="unitForm.is_default">
                Definir como configuração padrão
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </form>

      <template #modal-footer>
        <button type="button" class="btn btn-secondary" @click="showCreateUnitModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" @click="saveUnitConfig" :disabled="savingUnit">
          <span v-if="savingUnit" class="spinner-border spinner-border-sm me-1"></span>
          {{ editingUnit ? 'Salvar Alterações' : 'Criar Configuração' }}
        </button>
      </template>
    </b-modal>

    <!-- Modal para Criar/Editar Configuração de Profissional -->
    <b-modal
      v-model="showCreateProfessionalModal"
      :title="editingProfessional ? 'Editar Configuração de Profissional' : 'Nova Configuração de Profissional'"
      size="lg"
      @hidden="resetProfessionalForm"
    >
      <form @submit.prevent="saveProfessionalConfig">
        <!-- Dados do Profissional -->
        <div class="mb-4">
          <h6 class="mb-3">Dados do Profissional</h6>
          
          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Usuário" label-for="user_id">
                <b-form-select
                  id="user_id"
                  v-model="professionalForm.user_id"
                  :options="userOptions"
                  required
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Nome do Profissional" label-for="nome_profissional">
                <b-form-input
                  id="nome_profissional"
                  v-model="professionalForm.nome_profissional"
                  placeholder="Nome completo do profissional"
                  required
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="CPF" label-for="cpf_profissional">
                <b-form-input
                  id="cpf_profissional"
                  v-model="professionalForm.cpf_profissional"
                  v-mask="'###.###.###-##'"
                  placeholder="000.000.000-00"
                  maxlength="14"
                  required
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="CNS" label-for="cns_profissional">
                <b-form-input
                  id="cns_profissional"
                  v-model="professionalForm.cns_profissional"
                  placeholder="000000000000000"
                  maxlength="15"
                  required
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="CRM" label-for="crm_profissional">
                <b-form-input
                  id="crm_profissional"
                  v-model="professionalForm.crm_profissional"
                  placeholder="CRM do profissional"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Código da Especialidade" label-for="especialidade_codigo">
                <b-form-input
                  id="especialidade_codigo"
                  v-model="professionalForm.especialidade_codigo"
                  placeholder="Código da especialidade"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-group label="Nome da Especialidade" label-for="especialidade_nome">
                <b-form-input
                  id="especialidade_nome"
                  v-model="professionalForm.especialidade_nome"
                  placeholder="Nome da especialidade"
                />
              </b-form-group>
            </div>
            <div class="col-md-6">
              <b-form-group label="Procedimento Padrão" label-for="codigo_procedimento_default">
                <b-form-input
                  id="codigo_procedimento_default"
                  v-model="professionalForm.codigo_procedimento_default"
                  placeholder="Código do procedimento padrão"
                />
              </b-form-group>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <b-form-checkbox v-model="professionalForm.is_active">
                Profissional ativo para FHIR
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </form>

      <template #modal-footer>
        <button type="button" class="btn btn-secondary" @click="showCreateProfessionalModal = false">
          Cancelar
        </button>
        <button type="button" class="btn btn-primary" @click="saveProfessionalConfig" :disabled="savingProfessional">
          <span v-if="savingProfessional" class="spinner-border spinner-border-sm me-1"></span>
          {{ editingProfessional ? 'Salvar Alterações' : 'Configurar Profissional' }}
        </button>
      </template>
    </b-modal>
  </Layout>
</template>

<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import { authMixin } from "@/mixins/authMixin";
import { fhirUnitConfigService, fhirProfessionalConfigService } from "@/api/fhir";
import { userService } from "@/api/users";

export default {
  page: {
    title: "Configurações FHIR",
    meta: [{ name: "description", content: "Configurações FHIR e-SUS/RNDS" }],
  },
  components: { Layout, PageHeader },
  mixins: [authMixin],
  data() {
    return {
      title: "Configurações FHIR",
      items: [
        { text: "Integração", href: "/integration" },
        { text: "FHIR", active: true },
      ],
      
      // Estados de carregamento
      loading: false,
      loadingUnits: false,
      loadingProfessionals: false,
      savingUnit: false,
      savingProfessional: false,
      
      // Dados
      unitConfigs: [],
      professionalConfigs: [],
      users: [],
      
      // Modais
      showCreateUnitModal: false,
      showCreateProfessionalModal: false,
      
      // Edição
      editingUnit: null,
      editingProfessional: null,
      
      // Formulários
      unitForm: {
        nome_unidade: '',
        cnes: '',
        ine: '',
        codigo_ibge: '',
        rnds_endpoint: '',
        rnds_client_id: '',
        rnds_client_secret: '',
        rnds_environment: 'homolog',
        esus_endpoint: '',
        esus_usuario: '',
        esus_senha: '',
        esus_instalacao_counter_id: '',
        fhir_base_url: '',
        timezone: 'America/Sao_Paulo',
        is_active: true,
        is_default: false
      },
      
      professionalForm: {
        user_id: null,
        cpf_profissional: '',
        cns_profissional: '',
        nome_profissional: '',
        crm_profissional: '',
        especialidade_codigo: '',
        especialidade_nome: '',
        codigo_procedimento_default: '',
        is_active: true
      }
    };
  },
  
  computed: {
    userOptions() {
      const options = [{ value: null, text: 'Selecione um usuário' }];
      this.users.forEach(user => {
        if (user.role === 'DOCTOR' || user.role === 'ADMIN') {
          options.push({
            value: user.id,
            text: `${user.name} (${user.email})`
          });
        }
      });
      return options;
    }
  },
  
  async mounted() {
    await this.loadData();
  },
  
  methods: {
    async loadData() {
      await Promise.all([
        this.loadUnitConfigs(),
        this.loadProfessionalConfigs(),
        this.loadUsers()
      ]);
    },
    
    async loadUnitConfigs() {
      this.loadingUnits = true;
      try {
        const response = await fhirUnitConfigService.getAll();
        this.unitConfigs = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar configurações de unidade:', error);
        this.$toast.error('Erro ao carregar configurações de unidade');
      } finally {
        this.loadingUnits = false;
      }
    },
    
    async loadProfessionalConfigs() {
      this.loadingProfessionals = true;
      try {
        const response = await fhirProfessionalConfigService.getAll();
        this.professionalConfigs = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar configurações de profissionais:', error);
        this.$toast.error('Erro ao carregar configurações de profissionais');
      } finally {
        this.loadingProfessionals = false;
      }
    },
    
    async loadUsers() {
      try {
        const response = await userService.getUsers();
        this.users = response.data || [];
      } catch (error) {
        console.error('Erro ao carregar usuários:', error);
      }
    },
    
    // Métodos de Configuração de Unidade
    editUnitConfig(config) {
      this.editingUnit = config;
      Object.assign(this.unitForm, config);
      this.showCreateUnitModal = true;
    },
    
    async saveUnitConfig() {
      this.savingUnit = true;
      try {
        // Limpar campos vazios e preparar dados
        const formData = { ...this.unitForm };
        Object.keys(formData).forEach(key => {
          if (formData[key] === '') {
            formData[key] = null;
          }
        });
        
        if (this.editingUnit) {
          await fhirUnitConfigService.update(this.editingUnit.id, formData);
          this.$toast.success('Configuração atualizada com sucesso');
        } else {
          await fhirUnitConfigService.create(formData);
          this.$toast.success('Configuração criada com sucesso');
        }
        
        this.showCreateUnitModal = false;
        await this.loadUnitConfigs();
      } catch (error) {
        console.error('Erro ao salvar configuração:', error);
        this.$toast.error(error.response?.data?.detail || 'Erro ao salvar configuração');
      } finally {
        this.savingUnit = false;
      }
    },
    
    async toggleUnitDefault(config) {
      try {
        await fhirUnitConfigService.update(config.id, {
          is_default: !config.is_default
        });
        this.$toast.success(
          config.is_default 
            ? 'Configuração removida como padrão' 
            : 'Configuração definida como padrão'
        );
        await this.loadUnitConfigs();
      } catch (error) {
        console.error('Erro ao alterar configuração padrão:', error);
        this.$toast.error('Erro ao alterar configuração padrão');
      }
    },
    
    async deleteUnitConfig(config) {
      if (!confirm(`Tem certeza que deseja excluir a configuração "${config.nome_unidade}"?`)) {
        return;
      }
      
      try {
        await fhirUnitConfigService.delete(config.id);
        this.$toast.success('Configuração excluída com sucesso');
        await this.loadUnitConfigs();
      } catch (error) {
        console.error('Erro ao excluir configuração:', error);
        this.$toast.error('Erro ao excluir configuração');
      }
    },
    
    resetUnitForm() {
      this.editingUnit = null;
      this.unitForm = {
        nome_unidade: '',
        cnes: '',
        ine: '',
        codigo_ibge: '',
        rnds_endpoint: '',
        rnds_client_id: '',
        rnds_client_secret: '',
        rnds_environment: 'homolog',
        esus_endpoint: '',
        esus_usuario: '',
        esus_senha: '',
        esus_instalacao_counter_id: '',
        fhir_base_url: '',
        timezone: 'America/Sao_Paulo',
        is_active: true,
        is_default: false
      };
    },
    
    // Métodos de Configuração de Profissional
    editProfessionalConfig(config) {
      this.editingProfessional = config;
      Object.assign(this.professionalForm, {
        ...config,
        cpf_profissional: this.formatCPF(config.cpf_profissional)
      });
      this.showCreateProfessionalModal = true;
    },
    
    async saveProfessionalConfig() {
      this.savingProfessional = true;
      try {
        // Preparar dados removendo máscara do CPF
        const formData = { ...this.professionalForm };
        formData.cpf_profissional = formData.cpf_profissional.replace(/\D/g, '');
        
        await fhirProfessionalConfigService.createOrUpdate(formData);
        
        if (this.editingProfessional) {
          this.$toast.success('Configuração de profissional atualizada com sucesso');
        } else {
          this.$toast.success('Configuração de profissional criada com sucesso');
        }
        
        this.showCreateProfessionalModal = false;
        await this.loadProfessionalConfigs();
      } catch (error) {
        console.error('Erro ao salvar configuração de profissional:', error);
        this.$toast.error(error.response?.data?.detail || 'Erro ao salvar configuração de profissional');
      } finally {
        this.savingProfessional = false;
      }
    },
    
    async deleteProfessionalConfig(config) {
      if (!confirm(`Tem certeza que deseja excluir a configuração de "${config.nome_profissional}"?`)) {
        return;
      }
      
      try {
        // Como não há endpoint DELETE específico, vamos desativar
        await fhirProfessionalConfigService.createOrUpdate({
          ...config,
          is_active: false
        });
        this.$toast.success('Configuração de profissional removida com sucesso');
        await this.loadProfessionalConfigs();
      } catch (error) {
        console.error('Erro ao remover configuração de profissional:', error);
        this.$toast.error('Erro ao remover configuração de profissional');
      }
    },
    
    resetProfessionalForm() {
      this.editingProfessional = null;
      this.professionalForm = {
        user_id: null,
        cpf_profissional: '',
        cns_profissional: '',
        nome_profissional: '',
        crm_profissional: '',
        especialidade_codigo: '',
        especialidade_nome: '',
        codigo_procedimento_default: '',
        is_active: true
      };
    },
    
    // Utilitários
    formatCPF(cpf) {
      if (!cpf) return '';
      const cleanCPF = cpf.replace(/\D/g, '');
      return cleanCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
  }
};
</script>

<style scoped>
.font-size-48 {
  font-size: 48px;
}
</style>
